import axios from "axios";

import { api, apiRequest } from "@/lib/api";
import type {
  FoodItemResponse,
  FoodServingListItem,
  FoodServingPayload,
} from "@/lib/nutrition-api";
import type { FoodNutrientBasis } from "@/lib/nutrition-form-utils";

/**
 * Food data versioning (epic ZCS-403, console ticket ZCS-463).
 *
 * Deliberately mirrors `recipe-api.ts`: the backend contract was written to
 * match the recipe wire shapes field for field, so the console's existing
 * recipe components port across instead of being rebuilt. Where the two
 * disagree, the recipe spelling won — see §9 rows 7 and 8 of
 * `Columbus/docs/frontend-food-versioning.md`.
 *
 * The one place foods are NOT recipes: recipe blockers describe an incomplete
 * composition (no ingredients, no yield weight). A food has no composition to
 * be incomplete, so it emits two codes and does not inherit recipe's nine.
 */

// ---------------------------------------------------------------------------
// Read shapes
// ---------------------------------------------------------------------------

/**
 * Server-owned answer to "may this user do it, right now". Read these rather
 * than re-deriving publishability from `blockers` — the whole point of the
 * epic is that the UI stops inferring state the server owns. Added at the
 * console's request (contract §11.D).
 */
export interface FoodCapabilities {
  // The food's own profile and nutrient values. False on a reference-source
  // food (IFCT, USDA, USFDA) and on a retired one.
  can_edit?: boolean;
  // The food's servings. A serving is Zarity's own household-measure mapping,
  // not the reference's data, so this stays true on a reference-source food and
  // is false only when the food is retired. Absent on an API that predates it,
  // in which case readers fall back to `can_edit`.
  can_edit_servings?: boolean;
  // False when blockers exist OR nothing is staged. Not the same as "clean".
  can_publish?: boolean;
}

/**
 * The complete list as of contract §11.B — two codes, not a sample. `code` is
 * stable and safe to branch on; `message` is for humans and may change.
 *
 * Typed open (`string & {}`) on purpose: a food that gains a publish
 * precondition later gains a code here, and an unknown code must still render
 * its message rather than disappear.
 */
export type FoodBlockerCode =
  | "DUPLICATE_FOOD_NAME"
  | "DRAFT_UNAVAILABLE"
  | (string & {});

export interface FoodBlocker {
  code: FoodBlockerCode;
  message: string;
}

/**
 * The §2 block, added to every food read payload. All additive — no existing
 * field changed type or meaning — so this can be adopted at the console's own
 * pace.
 */
export interface FoodVersionFields {
  /**
   * Optimistic-concurrency token, bumped on EVERY staged write. This is the
   * `If-Match` value and nothing else. Never render it: it is not a revision
   * number and a clinician autosaving four times would watch it climb.
   */
  version: number;
  /** The published revision. Advances only on publish. THIS is the one to show, as `food_code · v{n}`. */
  food_version_number?: number | null;
  /**
   * What the staged draft will become on publish, or null when clean.
   *
   * Best-effort, and the contract is blunt about it (§8): since FV1 a publish
   * that changes nothing mints nothing, so this can be wrong even with a single
   * editor and no concurrency. Fine to decorate a pill with; never assert it.
   */
  draft_target_version_number?: number | null;
  has_unpublished_changes?: boolean;
  /**
   * Drafts are shared per food, not per user — one workspace, like recipes.
   * These three exist so we can warn before publishing someone else's staged
   * edit. Flat, not nested, matching the recipe payload.
   */
  draft_updated_by_id?: string | null;
  draft_updated_by?: string | null;
  draft_updated_at?: string | null;
  /**
   * Not selectable for new entries. Read it directly — do NOT infer it from
   * `is_active` + `is_deleted`, which describe whether a food is in use, not
   * what it is.
   *
   * Retirement is two behaviours and the second is easy to miss: excluded from
   * every picker, AND still rendering correctly on entries already pinned to it.
   */
  is_retired?: boolean;
  capabilities?: FoodCapabilities;
  blockers?: FoodBlocker[];
  /**
   * Coarse list of what the staged draft will change on publish (FV8). Same
   * vocabulary as version-history `changed` — lets a screen that only edits one
   * part (e.g. servings) warn that publishing also releases the rest. Absent /
   * empty when clean.
   */
  draft_changed?: FoodVersionChangedPart[];
  /**
   * The staged serving list — the servings as they would be AFTER publishing the
   * current draft (FV8). The live serving GET still returns published rows, so
   * this is the only way to preview staged serving edits. Null/absent when no
   * serving change is staged.
   */
  draft_servings?: FoodServingListItem[] | null;
}

export type VersionedFoodItem = FoodItemResponse & FoodVersionFields;

/**
 * On a meal plan / diet recall entry payload.
 *
 * Read the pin from the ENTRY, never from the food. `food_item_details
 * .food_version_number` nested inside an entry is the food's *current*
 * published number — the two agree right up until the food is republished,
 * which is exactly the moment reading the wrong one starts lying.
 */
export interface EntryPinnedVersionFields {
  /** Null on a comment-only entry, or a legacy row written before pinning existed. */
  pinned_version_number?: number | null;
  /**
   * `false` means the food has moved on since this entry was written. It is
   * NOT an error state — an old plan built on old data is correct and expected.
   * Null whenever `pinned_version_number` is null.
   */
  pinned_version_is_current?: boolean | null;
}

/** Which parts of the profile moved. Coarse by design — not a field-level diff. */
export type FoodVersionChangedPart =
  | "profile"
  | "nutrient_values"
  | "servings"
  | "tags";

export interface FoodVersionSummary {
  version_number: number;
  created_at: string;
  /**
   * Null wherever no human acted — bulk imports, the lazy backfill, an entry
   * re-pinning itself on save, and every version that predates this shipping.
   * No author is invented for those, so render the absence honestly.
   */
  created_by_id: string | null;
  created_by: string | null;
  is_current: boolean;
  changed: FoodVersionChangedPart[];
}

export interface FoodVersionNutrientValue {
  nutrient_name: string;
  nutrient_slug: string;
  unit: string;
  group: string;
  amount: string | null;
  daily_value_percentage: string | null;
}

/**
 * One historical version resolved in full, read from the immutable snapshot
 * tables — never from the live food.
 *
 * Two things to know before diffing two of these (contract §11.C):
 *
 * 1. There is no per-100 g / per-serve basis here, because the live food read
 *    has none either — that is a recipe-only concept. A diff view computes
 *    bases the same way it would from a live food.
 * 2. `nutrient_values` omits nutrient types superseded (merged onto a canonical
 *    row) since this version was minted. Two snapshots are diffable against
 *    each other, and not against a pre-merge export.
 */
export interface FoodVersionDetail {
  version_number: number;
  created_at: string;
  created_by_id: string | null;
  created_by: string | null;
  is_current: boolean;
  nutrient_basis: FoodNutrientBasis;
  energy_kcal: string;
  alcohol_g: string;
  caffeine_mg: string;
  water_g: string;
  nutrient_values: FoodVersionNutrientValue[];
  food_servings: FoodServingListItem[];
  /**
   * Written on every version and read by no production code today. Nutrition is
   * pinned to the version while tags are not, so an old plan currently shows
   * historical calories against today's allergen tags. Whether a pinned read
   * should consume this is a clinical call, not an engineering one — exposed
   * here only because it is what the version recorded.
   */
  tag_snapshot: unknown;
}

export interface FoodVersionListResponse {
  results: FoodVersionSummary[];
}

// ---------------------------------------------------------------------------
// Typed failures
// ---------------------------------------------------------------------------

export interface FoodConflictError extends Error {
  status: 409;
  currentFoodVersion?: number;
  currentFood?: VersionedFoodItem;
}

export interface FoodBlockerError extends Error {
  status: 400;
  blockers: FoodBlocker[];
}

/** 428 — the write carried no `If-Match` at all. A bug on our side, not a race. */
export interface FoodPreconditionRequiredError extends Error {
  status: 428;
}

const isVersionedFoodItem = (value: unknown): value is VersionedFoodItem =>
  Boolean(value) &&
  typeof value === "object" &&
  "id" in (value as object) &&
  "version" in (value as object);

const asRecord = (data: unknown): Record<string, unknown> =>
  data && typeof data === "object" && !Array.isArray(data)
    ? (data as Record<string, unknown>)
    : {};

const createFoodConflictError = (data: unknown): FoodConflictError => {
  const record = asRecord(data);
  const error = new Error("Food version conflict") as FoodConflictError;
  error.status = 409;
  // The 409 body carries the current version AND the current food, so a caller
  // can offer a reload without a second round-trip.
  error.currentFoodVersion =
    typeof record.version === "number" ? record.version : undefined;
  error.currentFood = isVersionedFoodItem(data) ? data : undefined;
  return error;
};

const createFoodBlockerError = (data: unknown): FoodBlockerError => {
  const record = asRecord(data);
  const error = new Error("Food has publish blockers") as FoodBlockerError;
  error.status = 400;
  error.blockers = Array.isArray(record.blockers)
    ? (record.blockers as FoodBlocker[])
    : [];
  return error;
};

export const isFoodConflictError = (
  error: unknown
): error is FoodConflictError =>
  Boolean(error) &&
  typeof error === "object" &&
  (error as Partial<FoodConflictError>).status === 409;

export const isFoodBlockerError = (error: unknown): error is FoodBlockerError =>
  Boolean(error) &&
  typeof error === "object" &&
  (error as Partial<FoodBlockerError>).status === 400 &&
  Array.isArray((error as Partial<FoodBlockerError>).blockers);

export const isFoodPreconditionRequiredError = (
  error: unknown
): error is FoodPreconditionRequiredError =>
  Boolean(error) &&
  typeof error === "object" &&
  (error as Partial<FoodPreconditionRequiredError>).status === 428;

/** Maps the three versioning failures onto typed errors; everything else rethrows untouched. */
const foodVersionRequest = async <T>(
  requestFn: () => Promise<{ data: T }>
): Promise<T> => {
  try {
    const response = await requestFn();
    return response.data;
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    const status = error.response?.status;
    if (status === 409) throw createFoodConflictError(error.response?.data);
    if (status === 428) {
      const missing = new Error(
        "Food write sent without If-Match"
      ) as FoodPreconditionRequiredError;
      missing.status = 428;
      throw missing;
    }
    if (status === 400) {
      const data = error.response?.data;
      if (Array.isArray(asRecord(data).blockers)) {
        throw createFoodBlockerError(data);
      }
    }
    throw error;
  }
};

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

// Backend compares the raw header string to str(version), so send the bare
// integer. A quoted ETag ("7") never matches and 409s on every save.
const ifMatch = (version: number) => ({ "If-Match": String(version) });

export const foodVersionAPI = {
  /**
   * Stage a draft. Writes to the draft, NOT to the live food — the live food
   * keeps serving its last published version to every picker and every new
   * entry until publish.
   *
   * One edge from the contract: do not deactivate a food and stage a draft in
   * the same request. The draft endpoints filter on `is_active`, so the draft
   * becomes unreachable and every later call 404s. Nothing is lost, but split
   * the two actions.
   */
  stage: async (
    id: number,
    version: number,
    data: Record<string, unknown>
  ): Promise<VersionedFoodItem> =>
    foodVersionRequest(() =>
      api.patch(`/nutrition/food-items/${id}/`, data, {
        headers: ifMatch(version),
      })
    ),

  /**
   * Apply the draft to the live food and mint a new version.
   *
   * Publishing a clean food is a no-op, not an error, and a publish that
   * changes nothing mints nothing. The idempotency key is accepted but not
   * stored, so it does not make a stale-`If-Match` replay safe — refresh the
   * version before retrying, the way the recipe publish path already does.
   */
  publish: async (
    id: number,
    version: number,
    idempotencyKey: string
  ): Promise<VersionedFoodItem> =>
    foodVersionRequest(() =>
      api.post(
        `/nutrition/food-items/${id}/publish/`,
        { idempotency_key: idempotencyKey },
        { headers: ifMatch(version) }
      )
    ),

  /** Drop the staged draft. The live food was never touched, so nothing rolls back. No-op when clean. */
  discard: async (id: number, version: number): Promise<VersionedFoodItem> =>
    foodVersionRequest(() =>
      api.post(`/nutrition/food-items/${id}/discard/`, undefined, {
        headers: ifMatch(version),
      })
    ),

  /**
   * Stage a NEW serving into the owning food's draft (contract §3, FV6/ZCS-464).
   *
   * Since FV6 a serving create no longer writes the row on the spot — it joins
   * the same draft as the food's own fields. Two things follow, and both are
   * why this lives here rather than on `nutritionAPI.foodServings`:
   *
   *  - `If-Match` carries the FOOD's `version` (the version of the `food_item`
   *    named in the body), not a serving-specific value — same counter and same
   *    428/409 rules as staging a scalar field.
   *  - The response is the food's own payload (`VersionedFoodItem`), not a
   *    serving object. Read `has_unpublished_changes` / `version` off it exactly
   *    as for any staged food edit. Nothing exists until the draft is published.
   *
   * A validation failure (weight floor, a removed serving unit, …) is a plain
   * field 400 and rethrows untouched for the caller's field-error handling — it
   * is not the `{ blockers: [] }` publish shape.
   */
  stageServingCreate: async (
    foodVersion: number,
    data: FoodServingPayload
  ): Promise<VersionedFoodItem> =>
    foodVersionRequest(() =>
      api.post(`/nutrition/food-servings/`, data, {
        headers: ifMatch(foodVersion),
      })
    ),

  /**
   * Stage an EDIT to an existing serving. `If-Match` carries the version of the
   * food that OWNS the serving (GET the serving or the food first if you do not
   * already hold it). Response is the food payload, same as {@link stageServingCreate}.
   */
  stageServingUpdate: async (
    servingId: number,
    foodVersion: number,
    data: Partial<FoodServingPayload>
  ): Promise<VersionedFoodItem> =>
    foodVersionRequest(() =>
      api.patch(`/nutrition/food-servings/${servingId}/`, data, {
        headers: ifMatch(foodVersion),
      })
    ),

  /**
   * Stage a serving RETIRE (contract FV7). Since FV7 a `DELETE` no longer
   * removes the row — it STAGES a retire into the food's draft, carrying the
   * owning food's `version` as If-Match and returning the food's own payload,
   * exactly like a staged edit. Publishing mints a version without the serving;
   * the previous version keeps it, so pinned plans still resolve.
   */
  stageServingRetire: async (
    servingId: number,
    foodVersion: number
  ): Promise<VersionedFoodItem> =>
    foodVersionRequest(() =>
      api.delete(`/nutrition/food-servings/${servingId}/`, {
        headers: ifMatch(foodVersion),
      })
    ),

  /**
   * Version history, newest first. Console-only — a patient token gets 403.
   *
   * Resolves for a retired (and soft-deleted) food, unlike most food reads:
   * history exists to explain plans that already exist, and a retired food is
   * exactly the case where the plans outlive the food.
   */
  versions: async (id: number): Promise<FoodVersionListResponse> =>
    apiRequest(() => api.get(`/nutrition/food-items/${id}/versions/`)),

  /** One historical version in full, as that version recorded it. Console-only; resolves for retired foods. */
  version: async (
    id: number,
    versionNumber: number
  ): Promise<FoodVersionDetail> =>
    apiRequest(() =>
      api.get(`/nutrition/food-items/${id}/versions/${versionNumber}/`)
    ),
};
